import React, { useCallback, useContext, useEffect } from "react"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import {
  BackIcon,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Link,
  ListItemText,
  Progress,
  Typography,
  UnorderedList,
  UnorderedListItem
} from "@carrotfertility/carotene-core"
import { DialogActionsBar, DialogCloseButton } from "@carrotfertility/carotene-core-x"
import { FormattedMessage, useIntl } from "react-intl"
import { useHistory } from "react-router-dom"
import { OriginDialogContext } from "#/components/telemedicine/pelvic-floor-therapy/OriginDialogContext"
import { useRegisterTelemedicineForPelvicFloorTherapy } from "#/components/telemedicine/pelvic-floor-therapy/hooks/useTelemedicineForPelvicFloorTherapyRegistration"
import { isMobileApp } from "#/lib/carrot-api/MobileNativeClient"
import { useOriginStepTracking } from "#/components/telemedicine/pelvic-floor-therapy/hooks/useOriginStepTracking"
import { useGetLocationDetails } from "#/pages/provider-finder/hooks/useGetLocationDetails"
import { ProviderFinderParam } from "#/pages/provider-finder/utils/providerFinderSearchUrl"

const ORIGIN_PRIVACY_NOTICE_URL = "https://www.theoriginway.com/privacy-policy"
const ORIGIN_TERMS_OF_SERVICE_URL = "https://www.theoriginway.com/terms"

export function YouAreLeavingCarrotStep(): JSX.Element {
  const { back } = useStateMachine(null)
  const { setOpenDialogFlow, officeId } = useContext(OriginDialogContext)
  const intl = useIntl()
  const {
    mutate: registerForPelvicFloorTherapy,
    data: registration,
    isLoading: isRegistering,
    isError: isRegistrationError
  } = useRegisterTelemedicineForPelvicFloorTherapy()
  const { trackButtonClick } = useOriginStepTracking("You are leaving Carrot")

  const register = useCallback(() => {
    registerForPelvicFloorTherapy()
  }, [registerForPelvicFloorTherapy])

  useEffect(() => {
    register()
  }, [register])

  const history = useHistory()
  const pfSearchParams = new URLSearchParams(history.location.search)
  const pfSearchLocation = pfSearchParams.get(ProviderFinderParam.Search)
  const { data: locationDetails, isLoading: isLocationLoading } = useGetLocationDetails(pfSearchLocation)
  const searchedState = locationDetails?.administrativeLevels?.level1short
  const isAwaitingSearchedState = pfSearchLocation && isLocationLoading

  let continueUrl = registration?.url
  if (continueUrl && officeId != null) {
    const url = new URL(continueUrl)
    url.searchParams.set("officeID", String(officeId))
    continueUrl = url.toString()
  }
  if (continueUrl && searchedState) {
    const url = new URL(continueUrl)
    url.searchParams.set("state", searchedState)
    continueUrl = url.toString()
  }
  const isContinueDisabled = isRegistering || isRegistrationError || !continueUrl || isAwaitingSearchedState

  const handleContinueClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (!continueUrl) {
      return
    }
    trackButtonClick({ name: "Continue to Origin", href: continueUrl })
    // Open the external site synchronously from the user gesture to avoid Safari's popup blocker
    if (isMobileApp()) {
      window.location.href = continueUrl
    } else {
      window.open(continueUrl, "_blank", "noopener,noreferrer")
    }
    setOpenDialogFlow(false)
  }

  const handleBackClick = () => {
    trackButtonClick({ name: "Back" })
    back()
  }

  const handleCloseIconClick = () => {
    trackButtonClick({ name: "Close (X)" })
    setOpenDialogFlow(false)
  }

  return (
    <>
      <DialogCloseButton onClick={handleCloseIconClick} />
      <DialogTitle>
        <FormattedMessage defaultMessage="You are leaving Carrot" />
      </DialogTitle>
      <DialogContent>
        <Typography>
          <FormattedMessage defaultMessage="You are about to leave Carrot and proceed to a third-party website operated by Origin." />
        </Typography>
        <Box>
          <Typography>
            <FormattedMessage defaultMessage="Please note:" />
          </Typography>
          <UnorderedList>
            <UnorderedListItem color="secondary">
              <ListItemText
                primary={intl.formatMessage({
                  defaultMessage: "Carrot is not responsible for Origin's content, security, or privacy practices."
                })}
              />
            </UnorderedListItem>
            <UnorderedListItem>
              <ListItemText
                primary={intl.formatMessage({
                  defaultMessage:
                    "Origin will be solely responsible for the collection, use, and disclosure of your personal data."
                })}
              />
            </UnorderedListItem>
            <UnorderedListItem>
              <ListItemText
                primary={intl.formatMessage(
                  {
                    defaultMessage:
                      "You can learn more about Origin's collection, use, and disclosure of personal data in their <pnLink>Privacy Notice</pnLink> and <tsLink>Terms of Service</tsLink>."
                  },
                  {
                    pnLink: (linkContent) => (
                      <Link target="_blank" color="inherit" href={ORIGIN_PRIVACY_NOTICE_URL}>
                        {linkContent}
                      </Link>
                    ),
                    tsLink: (linkContent) => (
                      <Link target="_blank" color="inherit" href={ORIGIN_TERMS_OF_SERVICE_URL}>
                        {linkContent}
                      </Link>
                    )
                  }
                )}
              />
            </UnorderedListItem>
          </UnorderedList>
        </Box>
        <Typography>
          <FormattedMessage defaultMessage="By continuing, you acknowledge and accept that Carrot is not liable or responsible for any actions or inactions taken by Origin." />
        </Typography>
        {isRegistrationError && (
          <Typography color="error">
            <FormattedMessage defaultMessage="We weren't able to complete your request. Please try again later." />
          </Typography>
        )}
      </DialogContent>
      <DialogActionsBar
        primaryAction={
          <Button
            onClick={handleContinueClick}
            type="submit"
            color="secondary"
            variant="contained"
            disabled={isContinueDisabled}
          >
            {isRegistering ? (
              <Progress size="1.25rem" color="inherit" />
            ) : (
              <FormattedMessage defaultMessage="Continue to Origin" />
            )}
          </Button>
        }
        tertiaryAction={
          <Button color="secondary" onClick={handleBackClick} variant="text" startIcon={<BackIcon />}>
            <FormattedMessage defaultMessage="Back" />
          </Button>
        }
      />
    </>
  )
}
